import React, { useState, useEffect } from 'react';
import { Form, Input, message, Spin } from 'antd';
import { SaveOutlined, LockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from '../../../utility/axios';
import '../../../styles/settings/profile.css';

const ChangePasswordModal = ({ visible, onClose, profileData }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (visible) {
            form.resetFields();
        }
    }, [visible, form]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            if (!profileData?.unique_identifier) {
                throw new Error('Profile data not loaded');
            }

            const response = await axios.post(`/api/set-password/${profileData.unique_identifier}/`, {
                current_password: values.currentPassword,
                new_password: values.newPassword,
                confirm_password: values.confirmPassword
            });

            message.success(response.data.message);
            form.resetFields();
            onClose();
            navigate('/login');
        } catch (error) {
            if (error.errorFields) {
                return;
            }
            console.error('Error changing password:', error);
            const errorMessage = error.response?.data?.error || t('profile.changePasswordModal.error');
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onClose();
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget && !loading) {
            handleCancel();
        }
    };

    if (!visible) return null;

    return (
        <div className="profile-modal-overlay" onClick={handleOverlayClick}>
            <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
                <Spin spinning={loading}>
                    <div className="profile-modal-header">
                        <div className="profile-modal-icon lock">
                            <LockOutlined />
                        </div>
                        <h3>{t('profile.changePasswordModal.title')}</h3>
                    </div>

                    <div className="profile-modal-content">
                        <Form form={form} layout="vertical" onFinish={handleSave}>
                            <div className="profile-modal-form">
                                <Form.Item
                                    name="currentPassword"
                                    label={null}
                                    rules={[{ required: true, message: t('profile.changePasswordModal.currentRequired') }]}
                                >
                                    <Input.Password
                                        className="profile-modal-password-input"
                                        placeholder={t('profile.changePasswordModal.currentPlaceholder')}
                                        autoComplete="current-password"
                                    />
                                </Form.Item>
                                <Form.Item
                                    name="newPassword"
                                    label={null}
                                    rules={[{ required: true, message: t('profile.changePasswordModal.newRequired') }]}
                                >
                                    <Input.Password
                                        className="profile-modal-password-input"
                                        placeholder={t('profile.changePasswordModal.newPlaceholder')}
                                        autoComplete="new-password"
                                    />
                                </Form.Item>
                                <Form.Item
                                    name="confirmPassword"
                                    label={null}
                                    dependencies={['newPassword']}
                                    rules={[
                                        { required: true, message: t('profile.changePasswordModal.confirmRequired') },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (!value || getFieldValue('newPassword') === value) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject(new Error(t('profile.changePasswordModal.passwordMismatch')));
                                            },
                                        }),
                                    ]}
                                >
                                    <Input.Password
                                        className="profile-modal-password-input"
                                        placeholder={t('profile.changePasswordModal.confirmPlaceholder')}
                                        autoComplete="new-password"
                                    />
                                </Form.Item>
                            </div>

                            <div className="profile-modal-actions">
                                <button
                                    type="button"
                                    className="profile-modal-cancel"
                                    onClick={handleCancel}
                                    disabled={loading}
                                >
                                    {t('profile.changePasswordModal.cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="profile-modal-save"
                                    disabled={loading}
                                >
                                    <SaveOutlined />
                                    {loading ? t('profile.changePasswordModal.changing') : t('profile.changePasswordModal.confirm')}
                                </button>
                            </div>
                        </Form>
                    </div>
                </Spin>
            </div>
        </div>
    );
};

export default ChangePasswordModal;
